import React, { createContext, useState, useContext, useEffect, useMemo, useRef, useCallback } from 'react';
import api from '../utils/api';
import { routeSessionCache } from '../utils/routeSessionCache';
import { phmLiveSessionCache } from '../components/ProductiveHours/phmLiveSessionCache';
import { startPresenceTracking } from '../utils/presence';
import { readAssumedUser, writeAssumedUser, AS_REAL_ACCOUNT, canAssumeUsers } from '../utils/assumeUser';
import { restartApp } from '../utils/restartApp';

const AuthContext = createContext();
const AUTH_ME_TIMEOUT_MS = 12000;
const AUTH_BOOTSTRAP_MAX_MS = 15000;

const clearClientSessionCaches = () => {
  try {
    phmLiveSessionCache.clear();
    routeSessionCache.clearAll();
  } catch (_) {
    /* best-effort cache wipe */
  }
};

/** The signed-in context, or null outside the provider (a piece that can work without knowing who is signed in). */
export const useAuthOptional = () => useContext(AuthContext) || null;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Admin-only "view the app as a user" (utils/assumeUser.js). `user` is the real signed-in account; while an Admin is
  // viewing as someone, `assumedUser` is that person as the server describes them (role, label, permissions) and
  // consumers get it as `value.user`, so every screen answers as they would. The server is answering as them too.
  const [assumedUser, setAssumedUser] = useState(null);
  const authGenerationRef = useRef(0);
  const fetchUserAbortRef = useRef(null);

  const invalidateAuthRequests = () => {
    authGenerationRef.current += 1;
    if (fetchUserAbortRef.current) {
      fetchUserAbortRef.current.abort();
      fetchUserAbortRef.current = null;
    }
  };

  const applySession = (nextUser) => {
    setUser(nextUser);
    setIsAuthenticated(true);
    setLoading(false);
  };

  const clearSession = useCallback(() => {
    invalidateAuthRequests();
    clearClientSessionCaches();
    setUser(null);
    setIsAuthenticated(false);
    setLoading(false);
    setAssumedUser(null);
    writeAssumedUser(null);
  }, []);

  // Start viewing as a person. The server checks that this is the system Admin and writes the audit row; after that the
  // app starts again from the home page as that person, so nothing on screen is left over from the Admin's own view.
  const assumeUser = useCallback(async (id) => {
    try {
      const res = await api.post('/api/auth/assume', { userId: id }, { headers: AS_REAL_ACCOUNT });
      const person = res.data && res.data.user;
      if (!person) return { success: false, error: 'Could not start the view' };
      clearClientSessionCaches();
      writeAssumedUser({ id: person.id, name: person.displayName || person.username });
      restartApp('/home');
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message || 'Could not start the view' };
    }
  }, []);

  const stopAssuming = useCallback(async () => {
    try { await api.post('/api/auth/assume/end', {}, { headers: AS_REAL_ACCOUNT }); } catch (_) { /* the audit row is best effort */ }
    clearClientSessionCaches();
    writeAssumedUser(null);
    restartApp('/home');
  }, []);

  useEffect(() => {
    void fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Never leave the app on a permanent full-page "Loading…" if /api/auth/me hangs.
  useEffect(() => {
    if (!loading) return undefined;
    const id = window.setTimeout(() => setLoading(false), AUTH_BOOTSTRAP_MAX_MS);
    return () => window.clearTimeout(id);
  }, [loading]);

  // On auth failures, clear the session.
  useEffect(() => {
    const interceptorId = api.interceptors.response.use(
      (res) => res,
      (error) => {
        const status = error.response?.status;
        const errMsg = error.response?.data?.error;
        const isInvalidOrExpiredToken = status === 401 || (status === 403 && errMsg === 'Invalid or expired token');
        if (isInvalidOrExpiredToken) {
          clearSession();
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(interceptorId);
  }, [clearSession]);

  // Audit: app open / leave / heartbeat while authenticated (leave works without Logout).
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    return startPresenceTracking('app');
  }, [isAuthenticated]);

  const fetchUser = async () => {
    const generation = authGenerationRef.current;
    if (fetchUserAbortRef.current) fetchUserAbortRef.current.abort();
    const controller = new AbortController();
    fetchUserAbortRef.current = controller;
    try {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        if (generation !== authGenerationRef.current) return;
        try {
          // Always the real account first (an empty X-Assume-User), so the Admin's own account is known even while
          // viewing as somebody else.
          const response = await api.get('/api/auth/me', {
            timeout: AUTH_ME_TIMEOUT_MS,
            signal: controller.signal,
            headers: AS_REAL_ACCOUNT
          });
          if (generation !== authGenerationRef.current) return;
          let viewed = null;
          if (readAssumedUser()) {
            if (response.data && canAssumeUsers(response.data.role)) {
              try {
                // With the stored person's id on it, this is that person as the server describes them.
                viewed = (await api.get('/api/auth/me', { timeout: AUTH_ME_TIMEOUT_MS, signal: controller.signal })).data;
              } catch (viewError) {
                if (generation !== authGenerationRef.current) return;
                if (viewError.code === 'ERR_CANCELED' || viewError.name === 'CanceledError') return;
                // The person is gone, or the view cannot be kept: back to the Admin's own view.
                writeAssumedUser(null);
              }
            } else {
              writeAssumedUser(null);
            }
          }
          if (generation !== authGenerationRef.current) return;
          setAssumedUser(viewed);
          setUser(response.data);
          setIsAuthenticated(true);
          return;
        } catch (error) {
          if (generation !== authGenerationRef.current) return;
          if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') return;
          const status = error.response?.status;
          const timedOut = error.code === 'ECONNABORTED';
          const errMsg = error.response?.data?.error;
          const isAuthError = status === 401 || (status === 403 && errMsg === 'Invalid or expired token');
          const transient = !error.response || status === 503 || status >= 500;
          if (isAuthError || timedOut) {
            setUser(null);
            setIsAuthenticated(false);
            return;
          }
          if (transient && attempt === 0) {
            await new Promise((resolve) => window.setTimeout(resolve, 1500));
            continue;
          }
          return;
        }
      }
    } finally {
      // Only the latest request may end the "Signing in" state. React StrictMode (development) runs the
      // mount effect twice, and the second call aborts the first: if the aborted one cleared `loading`,
      // the route guards saw "not signed in" for a moment and sent a direct link to /login, which then
      // forwarded to /home. A superseded or cancelled request leaves loading to the one that replaced it.
      if (generation === authGenerationRef.current && fetchUserAbortRef.current === controller) {
        fetchUserAbortRef.current = null;
        setLoading(false);
      }
    }
  };

  const authRequest = async (endpoint, body, fallbackError) => {
    try {
      invalidateAuthRequests();
      const response = await api.post(endpoint, body);
      const nextUser = response.data && response.data.user;
      if (!nextUser) {
        return { success: false, error: fallbackError };
      }
      applySession(nextUser);
      return { success: true };
    } catch (error) {
      const apiMsg = error.response?.data?.error;
      const networkMsg =
        !error.response && error.message
          ? `Cannot reach API (${error.config?.baseURL || 'http://localhost:5000'}). Is the backend running?`
          : '';
      return {
        success: false,
        error: apiMsg || networkMsg || error.message || fallbackError
      };
    }
  };

  const login = (username, password) =>
    authRequest('/api/auth/login', { username, password }, 'Login failed');

  const oktaLoginWithCode = (code, redirectUri) =>
    authRequest('/api/auth/okta/code', { code, redirect_uri: redirectUri }, 'Okta SSO failed');

  const register = (username, email, password) =>
    authRequest('/api/auth/register', { username, email, password }, 'Registration failed');

  const logout = () => {
    void api.post('/api/auth/logout').catch(() => {});
    clearSession();
  };

  const viewedAs = useMemo(() => (assumedUser ? { ...assumedUser, isAssumed: true } : null), [assumedUser]);

  const value = {
    user: viewedAs || user,
    // The real account, for anything that must not be fooled by the view - the navbar control that turns it off,
    // above all.
    realUser: user,
    // Who the Admin is viewing as ({ id, name, roleLabel }), or null.
    assumed: assumedUser ? { id: assumedUser.id, name: assumedUser.displayName || assumedUser.username || '', roleLabel: assumedUser.roleLabel || assumedUser.role || '' } : null,
    assumeUser,
    stopAssuming,
    loading,
    isAuthenticated,
    showNavbar: isAuthenticated,
    login,
    oktaLoginWithCode,
    register,
    logout,
    fetchUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
